import { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { theme } from '@frontend/theme';
import { Accordion, ModalControlModalProps, Text, Tray } from '@frontend/design-system';
import { FilterableCheckboxList } from '../../../components';
import { useGetCalendarV3QueriesData, useIsScheduleSmallModeWindow } from '../../../hooks';
import { CALENDAR_TRACKING_IDS } from '../../../trackingIds/calendar-tracking-ids';
import { QuickFilters } from '../../CalendarV3/components/header/quick-filters';
import { QuickFilterKey, QuickFilterListItem } from '../../CalendarV3/components/header/quick-filters/types';
import { EntityFilterItemLabel } from '../entity-filter-item-label';
import { useFilterPanelForm } from '../use-filter-panel-form';
import { CollapsibleFilterList } from './collapsible-filter-list';
import { CustomAccordionHeader } from './custom-accordion-header';
import { CustomTrayHeader } from './custom-tray-header';
import { FilterDrawerV2 } from './filter-drawer-v2';

const trackingIds = CALENDAR_TRACKING_IDS.headerBarFilter;

interface Props extends ModalControlModalProps {
  filterCount?: number;
}

const FilterDrawerV3 = ({ filterCount, ...modalProps }: Props) => {
  const { t } = useTranslation('scheduleCalendarFilters');
  const tokens = useTranslationTokens();
  const { isSmallModeWindow } = useIsScheduleSmallModeWindow();
  const { formField, syncFilters, applyFilters, resetFilters, isFiltersChanged, filterCounts, appliedFilters } =
    useFilterPanelForm();
  const { filterLists, calendarCounts } = useGetCalendarV3QueriesData();
  const { isOperatoryV3FlagEnabled } = SchedulerV3.Helpers.useGetV3OperatoryFeatureFlagDetails();

  useEffect(() => {
    if (modalProps.show) syncFilters();
    // NOTE: sync only when open the drawer to avoid overwrite unsaved changes
    // So don't add syncFilters to dependency array
  }, [modalProps.show]);

  const quickFilterList = useMemo<QuickFilterListItem[]>(
    () => [
      {
        key: QuickFilterKey.Unconfirmed,
        label: t('Unconfirmed'),
        iconName: 'pending',
        count: calendarCounts.unconfirmed,
        isActive:
          formField.values.apptStatuses?.length === 1 &&
          formField.values.apptStatuses[0] === QuickFilterKey.Unconfirmed,
        trackingId: CALENDAR_TRACKING_IDS.headerBarFilter.appointmentStatusQuickFilter,
      },
    ],
    [t, calendarCounts.unconfirmed, formField.values.apptStatuses]
  );

  const handleApplyClick = () => {
    applyFilters();
    modalProps.onClose();
  };

  const handResetClick = () => {
    resetFilters();
    modalProps.onClose();
  };

  const totalFilterCount = filterCounts.total;

  return (
    <Tray
      mountTarget='#main-content'
      {...modalProps}
      autoFocusTimeout={3600000 * 2}
      css={{ padding: 0, gap: 0 }}
      width='smallXL'
    >
      <CustomTrayHeader onClose={modalProps.onClose} filterCount={totalFilterCount} />
      <Tray.Body
        css={{
          borderBottom: `1px solid ${theme.colors.neutral20}`,
          borderTop: `1px solid ${theme.colors.neutral20}`,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isSmallModeWindow && (
          <div css={quickFilterContainerStyle}>
            <Text size='small' weight='bold'>
              {t('Quick Filters')}
            </Text>
            <div>
              <QuickFilters
                variant='compactCard'
                list={quickFilterList}
                onSelect={(status) =>
                  formField.seedValues({ apptStatuses: [...(formField.values.apptStatuses ?? []), status] })
                }
                onRemove={() => formField.seedValues({ apptStatuses: [] })}
              />
            </div>
          </div>
        )}
        <Accordion
          variant='main'
          shouldRenderBody
          chevronSize={16}
          css={{ padding: theme.spacing(2), '& > div:last-child': { marginBottom: 0 }, overflowY: 'auto' }}
        >
          <Accordion.Item value='appointment-status' trackingId={trackingIds.appointmentStatusAccordion}>
            <CustomAccordionHeader
              title={t('Appointment Status')}
              icon='pending-small'
              filterCount={filterCounts.apptStatuses}
            />
            <Accordion.Body>
              <FilterableCheckboxList
                listStyle={listStyle}
                fieldName='apptStatuses'
                checkListField={formField.getFieldProps('apptStatuses')}
                list={filterLists.appointmentStatuses}
                clearButtonTrackingId={trackingIds.clearAllAppointmentStatusButton}
                selectAllButtonTrackingId={trackingIds.selectAllAppointmentStatusButton}
              />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item value='appointment-type' trackingId={trackingIds.appointmentTypeAccordion}>
            <CustomAccordionHeader
              title={t('Appointment Type')}
              icon='tile-small'
              filterCount={filterCounts.apptTypes}
            />
            <Accordion.Body>
              <FilterableCheckboxList
                listStyle={listStyle}
                fieldName='apptTypes'
                list={filterLists.appointmentTypes}
                checkListField={formField.getFieldProps('apptTypes')}
                clearButtonTrackingId={trackingIds.clearAllAppointmentTypesButton}
                selectAllButtonTrackingId={trackingIds.selectAllAppointmentTypesButton}
                Label={EntityFilterItemLabel}
              />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item value='providers' trackingId={trackingIds.providerAccordion}>
            <CustomAccordionHeader title={t('Providers')} icon='user-small' filterCount={filterCounts.providers} />
            <Accordion.Body>
              <FilterableCheckboxList
                listStyle={listStyle}
                fieldName='providers'
                list={filterLists.providers}
                checkListField={formField.getFieldProps('providers')}
                clearButtonTrackingId={trackingIds.clearAllProvidersButton}
                selectAllButtonTrackingId={trackingIds.selectAllProvidersButton}
                Label={EntityFilterItemLabel}
              />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item value='insurance-status' trackingId={trackingIds.insuranceStatusAccordion}>
            <CustomAccordionHeader
              title={t('Insurance Status')}
              icon='shield-small'
              filterCount={filterCounts.insuranceStatuses}
              disabled
            />
            <Accordion.Body>
              <FilterableCheckboxList
                listStyle={listStyle}
                fieldName='insuranceStatuses'
                list={[]}
                checkListField={formField.getFieldProps('insuranceStatuses')}
                clearButtonTrackingId={trackingIds.clearAllInsuranceStatusesFilterButton}
                selectAllButtonTrackingId={trackingIds.selectAllInsuranceStatusesFilterButton}
              />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item value='operatory' trackingId={trackingIds.operatoryAccordion}>
            <CustomAccordionHeader
              title={tokens.operatory}
              icon='department-small'
              filterCount={filterCounts.operatories}
              disabled={!isOperatoryV3FlagEnabled}
            />
            <Accordion.Body>
              <FilterableCheckboxList
                listStyle={listStyle}
                fieldName='operatories'
                list={filterLists.operatories}
                checkListField={formField.getFieldProps('operatories')}
                clearButtonTrackingId={trackingIds.clearAllOperatoriesButton}
                selectAllButtonTrackingId={trackingIds.selectAllOperatoriesButton}
              />
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
        {isSmallModeWindow && totalFilterCount > 0 && (
          <div css={appliedFiltersContainerStyles}>
            <div>
              <Text size='small' weight='bold'>
                {t('Applied Filters ({{count}})', { count: totalFilterCount })}
              </Text>
            </div>
            <CollapsibleFilterList
              selectedFilters={appliedFilters.selectedFilters}
              onRemove={appliedFilters.removeFilter}
              maxRows={2}
            />
          </div>
        )}
      </Tray.Body>
      <Tray.Actions
        css={{ padding: theme.spacing(2) }}
        onPrimaryClick={handleApplyClick}
        disabledPrimary={!isFiltersChanged}
        primaryLabel={t('Apply')}
        onSecondaryClick={handResetClick}
        secondaryLabel={t('Reset')}
        primaryTrackingId={trackingIds.applyFiltersButton}
        secondaryTrackingId={trackingIds.resetFiltersButton}
      />
    </Tray>
  );
};

export const FilterDrawer = (props: Props) => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();

  if (!isScheduleV3FlagEnabled) {
    return <FilterDrawerV2 {...props} />;
  }

  return <FilterDrawerV3 {...props} />;
};

const listStyle = css({
  minHeight: 200,
  maxHeight: 'calc(100vh - 645px)',
  overflowY: 'auto',
});

const quickFilterContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.colors.neutral20}`,
});

const appliedFiltersContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  borderTop: `1px solid ${theme.colors.neutral20}`,
  padding: theme.spacing(2, 2, 1, 2),
  gap: theme.spacing(1),

  // Align at the bottom
  position: 'absolute',
  overflow: 'hidden',
  width: '100%',
  bottom: 0,
  backgroundColor: theme.colors.white,
  zIndex: theme.zIndex.low,
});
